import { request } from '@/fetch/core';
import { Method } from '@/fetch/core/constant';
import { ApiResponse } from '@/types';
import { EnableAndDisableStatus, LineOnlineStatus } from '@/utils/enum';

export interface MapManagePageRequest {
  startTime?: string;
  endTime?: string;
  provinceId?: number | null;
  cityId?: number | null;
  countryId?: number | null;
  companyCode?: string;
  areaCode?: string;
  stationNumber?: string;
  taskStatus?: string;
  mapVendor?: string;
  vehicleModelName?: string;
  durationOperator?: string;
  durationValue?: number | null;
  mileageOperator?: string;
  mileageValue?: number | null;
  batchCode?: string;
  id?: number | null;
  pageNum: number;
  pageSize: number;
}

export interface OperationRecordPageRequest {
  taskNumber: string;
  pageNum: number;
  pageSize: number;
}

export interface OperationRecordItem {
  id: number;
  operateSource: string;
  operateTypeName: string;
  operateUser: string;
  operateTime: string;
  operateUserName: string | null;
}

export interface AcceptTaskRequest {
  taskId: number;
  contactUser: string;
  contactPhone: string;
  mapVendor: string;
  estCompleteTime: string;
}

export interface CompleteTaskRequest {
  taskId: number;
  contactUser: string;
  contactPhone: string;
  completeTime: string;
}

export interface SubmitTaskListRequest {
  taskIdList: number[];
}

export interface DownloadTaskRequest {
  taskIdList: number[];
}

export interface GetTaskFollowerRequest {
  taskIdList: number[];
}

export interface BatchMarkRequest {
  taskIdList: number[];
  mark: string;
}

export interface GenerateRequirementNoRequest {
  taskIdList: number[];
}

export interface UpdateTaskTimeRequest {
  taskIdList: number[];
  expectDeliveryTime?: string | null;
  estDeliveryTime?: string | null;
}

export interface TaskDeployRequest {
  /** 路线ID列表 */
  taskIdList: number[];
  /** 是否上线 */
  online: LineOnlineStatus;
}

export interface CancelTaskRequest {
  /** 路线ID列表 */
  taskIdList: number[];
  /** 取消原因 */
  reason: string;
}

export interface ChangeStatusRequest {
  /** 路线ID列表 */
  taskIdList: number[];
  /** 1-启用 0-停用 */
  enable: EnableAndDisableStatus;
}

export interface MapVendorContactItem {
  taskStatus: string;
  contactUser: string;
  contactPhone: string;
  mapVendor: string;
  mapVendorName: string;
}

class MapManageApi {
  // 分页查询地图任务列表
  public async getMapManagePage(params: MapManagePageRequest): Promise<any> {
    const requestOptions = {
      path: '/k2/management/deployment/mapTask/page',
      method: Method.POST,
      body: params,
    };
    return request(requestOptions);
  }

  // 领取任务
  public async acceptTask(params: AcceptTaskRequest): Promise<any> {
    const requestOptions = {
      path: '/k2/management/deployment/mapTask/acceptTask',
      method: Method.POST,
      body: params,
    };
    return request(requestOptions);
  }

  // 完成任务
  public async completeTask(params: CompleteTaskRequest): Promise<any> {
    const requestOptions = {
      path: '/k2/management/deployment/mapTask/completeTask',
      method: Method.POST,
      body: params,
    };
    return request(requestOptions);
  }

  // 批量提交地图任务列表
  public async submitTaskList(params: SubmitTaskListRequest): Promise<any> {
    const requestOptions = {
      path: '/k2/management/deployment/mapTask/submitTaskList',
      method: Method.POST,
      body: params,
    };
    return request(requestOptions);
  }

  // 批量提交地图任务列表
  public async submitSimulation(params: SubmitTaskListRequest): Promise<any> {
    const requestOptions = {
      path: '/k2/management/deployment/mapTask/submitSimulation',
      method: Method.POST,
      body: params,
    };
    return request(requestOptions);
  }

  public async reVerify(params: SubmitTaskListRequest): Promise<any> {
    const requestOptions = {
      path: '/k2/management/deployment/mapTask/reVerify',
      method: Method.POST,
      body: params,
    };
    return request(requestOptions);
  }

  // 下载线路文本描述
  public async downloadTaskRoute(params: DownloadTaskRequest): Promise<any> {
    const requestOptions = {
      path: '/k2/management/deployment/mapTask/downloadTaskRoute',
      method: Method.POST,
      body: params,
    };
    return request(requestOptions);
  }

  // 下载txt文件
  public async downloadShpFile(params: DownloadTaskRequest): Promise<any> {
    const requestOptions = {
      path: '/k2/management/deployment/mapTask/downloadShpFile',
      method: Method.POST,
      body: params,
    };
    return request(requestOptions);
  }

  // 查询任务跟进人
  public async getTaskFollower(params: GetTaskFollowerRequest): Promise<any> {
    const requestOptions = {
      path: '/k2/management/deployment/mapTask/getTaskContact',
      method: Method.POST,
      body: params,
    };
    return request(requestOptions);
  }

  // 获取不同地图供应商对应的默认联系人和联系方式
  public async getMapVendorContact(): Promise<
    ApiResponse<MapVendorContactItem[]>
  > {
    const requestOptions = {
      path: '/k2/management/deployment/mapTask/mapVendorContact',
      method: Method.POST,
    };
    return request(requestOptions);
  }

  // 修改供应商
  public async modifyMapVendor(params: {
    taskIdList: number[];
    mapVendor: string;
    contactUser: string;
    contactPhone: string;
    estCompleteTime: string;
    estDeliveryTime: string;
  }): Promise<ApiResponse<any>> {
    const requestOptions = {
      path: '/k2/management/deployment/mapTask/modifyMapVendor',
      method: Method.POST,
      body: params,
    };
    return request(requestOptions);
  }

  // 路线按站点批量标注
  public async batchMark(params: BatchMarkRequest): Promise<ApiResponse<null>> {
    const requestOptions = {
      path: '/k2/management/deployment/mapTask/batchMark',
      method: Method.POST,
      body: params,
    };
    return request(requestOptions);
  }

  // 生成同批需求编号
  public async generateRequirementNo(
    params: GenerateRequirementNoRequest,
  ): Promise<ApiResponse<null>> {
    const requestOptions = {
      path: '/k2/management/deployment/mapTask/generateRequirementNo',
      method: Method.POST,
      body: params,
    };
    return request(requestOptions);
  }

  // 更新任务上线时间（期望/预计）
  public async updateTaskTime(
    params: UpdateTaskTimeRequest,
  ): Promise<ApiResponse<null>> {
    const requestOptions = {
      path: '/k2/management/deployment/mapTask/updateTaskTime',
      method: Method.POST,
      body: params,
    };
    return request(requestOptions);
  }

  // 批量上线任务
  public async taskDeploy(
    params: TaskDeployRequest,
  ): Promise<ApiResponse<null>> {
    const requestOptions = {
      path: '/k2/management/deployment/mapTask/taskDeploy',
      method: Method.POST,
      body: params,
    };
    return request(requestOptions);
  }

  // 取消任务
  public async cancelTask(
    params: CancelTaskRequest,
  ): Promise<ApiResponse<null>> {
    const requestOptions = {
      path: '/k2/management/deployment/mapTask/cancelTask',
      method: Method.POST,
      body: params,
    };
    return request(requestOptions);
  }

  // 启用/停用线路
  public async changeStatus(
    params: ChangeStatusRequest,
  ): Promise<ApiResponse<null>> {
    const requestOptions = {
      path: '/k2/management/deployment/mapTask/changeStatus',
      method: Method.POST,
      body: params,
    };
    return request(requestOptions);
  }

  // 分页查询操作记录
  public async getOperationRecordPage(
    params: OperationRecordPageRequest,
  ): Promise<
    ApiResponse<{
      list: OperationRecordItem[];
      total: number;
      pages: number;
      pageNum: number;
      pageSize: number;
    }>
  > {
    const requestOptions = {
      path: '/k2/management/deployment/mapTask/pageOperationLog',
      method: Method.POST,
      body: params,
    };
    return request(requestOptions);
  }
}

export const mapManageApi = new MapManageApi();
